
import { Component, Mixins } from 'vue-property-decorator'
import IconsMixin from '@/mixins/IconsMixin.vue'
import AuthMixin from '@/mixins/AuthMixin.vue'
import UserCompaniesMixin from '@/mixins/UserCompaniesMixin.vue'
import LoadingPage from '@/components/LoadingPage.vue'
import SaveButton from '@/components/SaveButton.vue'
import LabeledValue from '@/components/LabeledValue.vue'
import ProductSelector from '@/components/Order/ProductSelector.vue'
import QuantityTypeSelector from '@/components/Order/QuantityTypeSelector.vue'
import QuantityComboBox from '@/components/QuantityComboBox.vue'
import {
  Company,
  Order,
  OrderCollection,
  OrderEntry,
  OrderStatusEnum,
  OrderTypeEnum,
  PriceList,
  QuickbooksSetting,
  QuickbooksSettingCollection,
  CompanyTypeEnum,
  PriceListCollection,
  CompanyCollection,
  QuickbooksExportCollection,
  QuickbooksExport
} from '@/firestore'
import moment from 'moment'
import { dateService } from '@/services/date.service'
import { quickbooksExport } from '@/services/quickbooksExport.service'

@Component({
  components: {
    SaveButton,
    LoadingPage,
    LabeledValue,
    ProductSelector,
    QuantityTypeSelector,
    QuantityComboBox
  }
})
export default class QuickbooksExportEdit extends Mixins(IconsMixin, AuthMixin, UserCompaniesMixin) {
  public isLoading: boolean = false
  public quickbooksSetting: QuickbooksSetting = {} as QuickbooksSetting
  public errors: string[] = []
  public displayHelperText: boolean = false
  public isExporting: boolean = false
  public vendors: Company[] = []
  public priceLists: PriceList[] = []
  public tabs = null
  public menu: boolean = false
  public dates: string[] = [moment().day(0).format('YYYY-MM-DD'), moment().format('YYYY-MM-DD')]
  public orders: Order[] = []
  public missingVendorSettings = false
  public missingCategorySettings = false

  public showMissingInvoiceNumberModal = false
  public savedInvoiceNumbers = false
  public missingInvoiceNumberOrders: Order[] = []

  public showPriceModal = false
  public missingPriceOrderEntries: OrderEntry[] = []

  public showAlreadyExportedModal = false
  public alreadyExportedOrders: Order[] = []
  public excludeAlreadyOrdered = false

  public skipExportedCheck = false

  public months = dateService.months('abbreviatedMonths')
  public headers = [
    {text: '', sortable: false, value: 'icon'},
    {text: 'Order #', value: 'number'},
    {text: 'Vendor', value: 'assignedCompanyId'},
    {text: 'Date Placed', value: 'datePlaced'},
    {text: 'Status', value: 'status'}
  ]

  public get dateRangeText() {
    return this.dates.join(' ~ ')
  }

  public get displayedOrders() {
    return this.orders.sort((a: Order, b: Order) => {
      if (a.status === OrderStatusEnum.DRAFT || b.status === OrderStatusEnum.DRAFT) {
        if (a.status === OrderStatusEnum.DRAFT && b.status === OrderStatusEnum.DRAFT) {
          if (!a.datePlaced) {
            return 1
          } else if (!b.datePlaced) {
            return -1
          } else {
            return b.datePlaced.getTime() - a.datePlaced.getTime()
          }
        } else if (a.status === OrderStatusEnum.DRAFT) {
          return -1
        } else if (b.status === OrderStatusEnum.DRAFT) {
          return 1
        }
      }

      if (!a.datePlaced) {
        return 1
      } else if (!b.datePlaced) {
        return -1
      } else {
        return b.datePlaced.getTime() - a.datePlaced.getTime()
      }
    })
  }

  //METHODS
  public async mounted() {
    this.isLoading = true
    const quickbooksSettingCollection = new QuickbooksSettingCollection((await this.$firebase).firestore())
    const quickbooksSetting = await quickbooksSettingCollection.getQuickbooksSettingByRestaurantCompanyId(this.userCompany.id)
    if (quickbooksSetting) {
      this.quickbooksSetting = quickbooksSetting
    } else {
      this.quickbooksSetting = new QuickbooksSetting(this.userCompany.id)
      this.displayHelperText = true
    }

    await this.updateStats()

    const companyCollection = new CompanyCollection((await this.$firebase).firestore())
    this.vendors = await companyCollection.listCompaniesByType(CompanyTypeEnum.VENDOR)

    const priceListCollection = new PriceListCollection((await this.$firebase).firestore())
    this.priceLists = await priceListCollection.listPriceListsByCompanyId(this.userCompany.id, CompanyTypeEnum.RESTAURANT)

    if (
        !this.quickbooksSetting.categorySettings
        || !Object.values(this.quickbooksSetting.categorySettings).every(setting => setting)
    ) {
      this.missingCategorySettings = true
    }

    if (
        !this.quickbooksSetting.vendorSettings
        || !Object.values(this.quickbooksSetting.vendorSettings).every(setting =>
            setting.quickbooksVendorName && setting.quickbooksExpenseAccount
        )
    ) {
      this.missingVendorSettings = true
    }

    this.isLoading = false
  }

  public async updateStats() {
    if (!this.dates[0] || !this.dates[1]) {
      return
    }

    this.isLoading = true
    this.menu = false
    const dates = this.getDateRange(this.dates)
    const beginDate = dates[0].toISOString()
    const endDate = dates[1].toISOString()

    const orderCollection = new OrderCollection((await this.$firebase).firestore())
    const orders: Order[] = await orderCollection.listByDateRangeAndRestaurantId(beginDate, endDate, this.userCompany.id, OrderTypeEnum.VENDOR)
    this.orders = orders
    this.isLoading = false
  }


  public async saveAndExport(missingPriceOrderEntries: OrderEntry[]) {
    this.errors = []
    if (!missingPriceOrderEntries.every(e => typeof e.pricePerUnit === 'number' && e.pricePerUnit >= 0)) {
      this.errors.push('Before clicking Save And Export please enter a Price per Unit that is zero or greater, for every item listed')
      return
    }

    this.saveOrdersOfEntries(missingPriceOrderEntries)
    this.exportFile(true)
  }

  public async saveOrders(orders: Order[]) {
    const orderCollection = new OrderCollection((await this.$firebase).firestore())
    orders.map(async order =>
        await orderCollection.save(order)
    )
  }

  public async saveOrdersOfEntries(updatedOrderEntries: OrderEntry[]) {
    const orderCollection = new OrderCollection((await this.$firebase).firestore())
    // can we improve this?
    this.orders.map(async order => {
      if (updatedOrderEntries.find(entry => order.orderEntries.find(e => e.id === entry.id))) {
        await orderCollection.save(order)
      }
    })
  }

  public async createQuickbooksExport() {
    const quickbooksExportCollection = new QuickbooksExportCollection((await this.$firebase).firestore())
    const qbe = new QuickbooksExport(this.user.uid, this.userCompany.id)
    qbe.orderIds.push(
        ...this.orders.map(o => o.id)
    )
    qbe.exportedDate = new Date()

    await quickbooksExportCollection.save(qbe)
  }

  public convertToHeader(group: number) {
    if (group === 1000001) {
      return 'Drafts'
    } else if (!group || group === 1000000) {
      return 'No Date Found'
    }

    return `${this.months[parseInt(group.toString().slice(4), 10) - 1]} ${group.toString().slice(0, 4)}`
  }

  private getDateRange(dates: string[]) {
    const firstDate = moment(this.dates[0], 'YYYY-MM-DD')
    const secondDate = moment(this.dates[1], 'YYYY-MM-DD')

    if (firstDate.isBefore(secondDate)) {
      return [firstDate, secondDate.endOf('day')]
    } else {
      return [secondDate, firstDate.endOf('day')]
    }
  }

  public lookupVendorName(vendorCompanyId: string, entries: OrderEntry[]): string {
    const vendor = this.vendors.find((c: Company) => c.id === vendorCompanyId)
    return vendor ? vendor.displayName + this.lookupRepName(entries) : ''
  }

  public lookupRepName(entries: OrderEntry[]): string {
    const entry = entries.find(item => item.priceListId)

    if (entry && entry.priceListId) {
      const priceList = this.lookupPriceList(entry.priceListId)
      if (priceList && priceList.vendorRepName) {
        return ` - ${priceList.vendorRepName}`
      }
    }

    return ''
  }

  public lookupPriceList(id: string): PriceList {
    const priceList = this.priceLists.find(plist => plist.id === id)
    return priceList ? priceList : {} as PriceList
  }

  public async exportClicked() {
    this.hide()
    this.missingInvoiceNumberOrders = []
    this.alreadyExportedOrders = []
    this.missingPriceOrderEntries = []
    this.excludeAlreadyOrdered = false
    this.skipExportedCheck = false
    this.savedInvoiceNumbers = false

    if (!this.orders || !this.orders.length) {
      return
    }

    this.exportFile()
  }

  public async exportFile(skipPriceCheck?: boolean, excludeZeroes?: boolean) {
    // this is currently working because of the way we call it, but if exclude order stuff wasnt working right this would be a mistake to download
    if (skipPriceCheck) {
      this.isExporting = true

      await this.promptDownload(this.orders)

      this.hide()
      this.isExporting = false
    } else {
      if (!this.excludeAlreadyOrdered) {
        this.alreadyExportedOrders = await this.getOrdersThatHaveBeenExported(this.orders)
      }

      if (
          !this.skipExportedCheck
          && !this.excludeAlreadyOrdered
          && this.alreadyExportedOrders
          && this.alreadyExportedOrders.length
      ) {
        this.hide()
        this.showAlreadyExportedModal = true
        this.isExporting = false
      } else {
        if (!this.skipExportedCheck && this.excludeAlreadyOrdered) {
          this.orders = this.orders.filter(order => !this.alreadyExportedOrders.find(o => o.id === order.id))
        }

        if (!this.savedInvoiceNumbers) {
          this.missingInvoiceNumberOrders = this.getOrdersWithNoInvoiceNumber(this.orders)

          if (
              this.missingInvoiceNumberOrders
              && this.missingInvoiceNumberOrders.length
          ) {
            this.hide()
            this.showMissingInvoiceNumberModal = true
            return
          }
        }

        const missingPriceOrderEntries = this.getOrderEntriesWithNoPrice([...this.orders])
        this.missingPriceOrderEntries = missingPriceOrderEntries
        if (excludeZeroes && missingPriceOrderEntries && missingPriceOrderEntries.length) {
          this.isExporting = true

          const ordersWithPricesOnEntries = this.orders.map(order => {
            order.orderEntries = order.orderEntries.filter(entry => entry.pricePerUnit)
            if (order.orderEntries && order.orderEntries.length) {
              return order
            }
          }).filter(o => o) as Order[]

          await this.promptDownload(ordersWithPricesOnEntries)

          this.hide()
          this.isExporting = false
        } else if (missingPriceOrderEntries && missingPriceOrderEntries.length) {
          this.hide()
          this.showPriceModal = true
        } else {
          this.isExporting = true

          await this.promptDownload(this.orders)

          this.hide()
          this.isExporting = false
        }
      }
    }
  }

  private async promptDownload(orders: Order[]) {
    if (!orders || !orders.length) {
      return
    }

    await quickbooksExport(orders, this.quickbooksSetting)
    await this.createQuickbooksExport()
  }

  private async getOrdersThatHaveBeenExported(orders: Order[]) {
    const quickbooksExportCollection = new QuickbooksExportCollection((await this.$firebase).firestore())
    const exportedOrdersPromise = await orders.map(async order => {
      const exports = await quickbooksExportCollection.listQuickbooksExportsByOrderIdAndCompanyId(order.id, this.userCompany.id)
      if (exports && exports.length) {
        return order
      }
    })
    const exportedOrders = await Promise.all(exportedOrdersPromise)

    return exportedOrders.filter(o => o) as Order[]
  }

  private getOrdersWithNoInvoiceNumber(orders: Order[]) {
    return orders.map((order: Order) => order && !order.invoiceNumber ? order : null).filter(o => o) as Order[]
  }

  private getOrderEntriesWithNoPrice(orders: Order[]) {
    return orders.reduce((acc: OrderEntry[], order: Order) => {
      const entries = order.orderEntries.map(entry => {
        if (entry && (typeof entry.pricePerUnit !== 'number' || entry.pricePerUnit === 0)) {
          entry.pricePerUnit = 0
          return entry
        }
      }).filter(e => e) as OrderEntry[]

      return entries && entries.length ? [...acc, ...entries] : acc
    }, [] as OrderEntry[])
  }

  public hide() {
    this.showPriceModal = false
    this.showMissingInvoiceNumberModal = false
    this.showAlreadyExportedModal = false
  }
}
